import type { ApiCreditPackIdentifier } from '#/api/types/ApiCreditPackIdentifier';
import type { ApiInvoiceId, ApiOrgId } from '#/api/types/ApiTypedId';
import type { StaffOrgDashboardApiInvoice } from '#/modules/staff/orgDashboard/invoices/api/types/StaffOrgDashboardApiInvoice';
import { LISTING_TYPE_URL_SEGMENT_MAP } from '#/utils/constants/general/listingTypeUrlSegmentMap';
import { getJson } from '#/utils/http/getJson';
import { postJson } from '#/utils/http/postJson';
import { putJson } from '#/utils/http/putJson';
import { pathWithParams } from '#/utils/url/pathWithParams';

import type { StaffApiActionLogEntryApiResponse } from './types/StaffApiActionLogEntryApiResponse';
import type { StaffApiCredit } from './types/StaffApiCredit';
import type { StaffApiCreditPack } from './types/StaffApiCreditPack';

export function staffApiCreateSupportCase({
  listingType,
  listingId,
  reason,
  queueItemId,
}: {
  listingType: 'JOB' | 'VOLOP' | 'INTERNSHIP' | 'ORG';
  listingId: string;
  reason: string;
  queueItemId: string | null | undefined;
}): Promise<void> {
  return postJson(
    `/data/staff/${LISTING_TYPE_URL_SEGMENT_MAP[listingType]}/${listingId}/create-support-case`,
    {
      reason,
      queueItemId,
    },
  );
}

export type StaffApiExternalReportDefinition = {
  name: string;
  slug: string;
  url: string;
  height: number;
};

export function staffApiFetchExternalReportsMetadata(): Promise<
  StaffApiExternalReportDefinition[]
> {
  // @ts-expect-error TS(2345): Argument of type '({ charts }: { charts: any; }) =... Remove this comment to see the full error message
  return getJson('/data/staff/reports/external').then(({ charts }) => charts);
}

export function staffApiFetchHistory(
  objectType: string,
  objectId: string,
  before?: string,
): Promise<StaffApiActionLogEntryApiResponse> {
  return getJson(
    pathWithParams('/data/staff/action-log-entries', {
      objectType,
      objectId,
      before,
    }),
  );
}

export function staffApiFetchInvoices({
  number,
  outstanding,
}: {
  number: string;
  outstanding: boolean;
}): Promise<StaffOrgDashboardApiInvoice[]> {
  return getJson(
    `/data/staff/invoices?number=${number}&outstanding=${outstanding.toString()}`,
    // @ts-expect-error TS(2345): Argument of type '({ invoices }: { invoices: any; ... Remove this comment to see the full error message
  ).then(({ invoices }) => invoices);
}

export function staffApiVoidLineItem(lineItemId: string) {
  return postJson('/data/staff/void-line-items', {
    lineItemIds: [lineItemId],
  });
}

export function staffApiUseCreditForLineItem(lineItemId: string) {
  return postJson('/data/staff/use-credits', {
    lineItemIds: [lineItemId],
  });
}

export function staffApiAllowPartialPayments(
  invoiceId: ApiInvoiceId,
  allow: boolean,
) {
  const url = allow
    ? `/data/staff/invoices/${invoiceId}/allow-partial-payments`
    : `/data/staff/invoices/${invoiceId}/disallow-partial-payments`;

  return postJson(url);
}

export function staffApiFetchCreditPack(
  orgId: ApiOrgId,
  packId: ApiCreditPackIdentifier,
): Promise<{
  pack: StaffApiCreditPack;
  credits: StaffApiCredit[];
}> {
  return getJson(`/data/staff/credits/${packId}`);
}

export function staffApiCancelCredits(creditIds: string[]) {
  return putJson(`/data/staff/credits`, {
    creditIds,
  });
}

export function staffApiFetchActions(
  // TODO: Fix this the next time the file is edited.
  // oxlint-disable-next-line typescript/no-explicit-any
  params: Record<string, any>,
  before?: string,
): Promise<StaffApiActionLogEntryApiResponse> {
  return getJson(
    pathWithParams('/data/staff/action-log-entries', { ...params, before }),
  );
}
